import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useHospitalAuth } from '../../contexts/HospitalAuthContext';
import { useHospitalBranding } from '../../contexts/HospitalBrandingContext';
import { useData } from '../../contexts/DataContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { 
  LogOut,
  Stethoscope,
  BarChart3,
  FileText,
  Users,
  Calendar,
  Settings,
  ClipboardList,
  Clock,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

import DoctorStatsCards from '../../components/doctor/DoctorStatsCards';
import PatientSubmissionsList from '../../components/doctor/PatientSubmissionsList';
import SubmissionDetailsAndFeedback from '../../components/doctor/SubmissionDetailsAndFeedback';
import DoctorQuickActions from '../../components/doctor/DoctorQuickActions';
import DoctorRecentActivity from '../../components/doctor/DoctorRecentActivity';
import AdminOverviewTab from '../../components/admin/AdminOverviewTab';
import AdminPatientsTab from '../../components/admin/AdminPatientsTab';
import AdminDoctorsTab from '../../components/admin/AdminDoctorsTab';
import AppointmentsTab from '../../components/admin/AppointmentsTab';
import AdminSubmissionsTab from '../../components/admin/AdminSubmissionsTab';
import AdminSettingsTab from '../../components/admin/AdminSettingsTab';
import DoctorSettingsTab from '../../components/doctor/DoctorSettingsTab';
import CampSubmissions from '../../components/doctor/CampSubmissions';
import Attendance from '../../components/doctor/Attendance';

const DoctorDashboard = () => {
  const { profile, signOut } = useAuth();
  const { hospitalUser, hospital, hospitalSignOut } = useHospitalAuth();
  const { branding, getLogoUrl } = useHospitalBranding();
  const { submissions, patients, doctors, updateSubmission, fetchSubmissions, fetchPatients, fetchDoctors, addDoctor, updateDoctor } = useData();
  const navigate = useNavigate();
  
  // Use hospitalUser if available (hospital login), otherwise use profile (regular login)
  const currentUser = hospitalUser || profile;
  const userRole = hospitalUser?.role || profile?.role || '';
  
  // Role checks (handle both uppercase CHIEF_DOCTOR and lowercase chief_doctor)
  const roleUpper = (userRole || '').toUpperCase();
  const roleLower = (userRole || '').toLowerCase();
  const isChiefDoctor = roleUpper === 'CHIEF_DOCTOR' || roleLower === 'chief_doctor';
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [nextAppointment, setNextAppointment] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [scheduledAppointmentsCount, setScheduledAppointmentsCount] = useState(0);
  const [campSubmissionsCount, setCampSubmissionsCount] = useState(0);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [leaveRequestsCount, setLeaveRequestsCount] = useState(0);
  const [leaveRequestsPendingCount, setLeaveRequestsPendingCount] = useState(0);
  const [leaveRequestsApprovedCount, setLeaveRequestsApprovedCount] = useState(0);
  const [campSubmissionsInitialTab, setCampSubmissionsInitialTab] = useState('form');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [attendanceInitialTab, setAttendanceInitialTab] = useState('checkin');
  const [attendanceKey, setAttendanceKey] = useState(0);
  const [viewAsAdmin, setViewAsAdmin] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [planType, setPlanType] = useState(null);

  // Fetch hospital plan type and trial info
  useEffect(() => {
    const fetchHospitalPlanInfo = async () => {
      if (hospital?.id || hospital?.slug) {
        try {
          const slug = hospital?.slug || branding?.slug;
          if (slug) {
            const data = await apiRequest(`get_hospital_by_slug.php?slug=${slug}`);
            if (data.success && data.hospital) {
              setPlanType(data.hospital.plan_type || 'TRIAL');
              
              // Set trial info if plan type is TRIAL
              if (data.hospital.plan_type === 'TRIAL' && data.hospital.trial_end_at && data.hospital.is_trial_active) {
                const trialEnd = new Date(data.hospital.trial_end_at);
                const now = new Date();
                const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
                
                setTrialInfo({
                  endDate: trialEnd,
                  daysLeft: daysLeft > 0 ? daysLeft : 0,
                  isActive: data.hospital.is_trial_active
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching hospital plan info:', error);
        }
      }
    };
    
    fetchHospitalPlanInfo();
  }, [hospital, branding]);

  // Define all fetch functions before useEffect that uses them
  const fetchAppointmentsCount = useCallback(async () => {
    try {
      // Build URL with appropriate parameters
      let url = 'get_appointments_count.php';
      const params = [];
      
      // For chief doctor: use view_as_admin parameter to control what they see
      if (isChiefDoctor) {
        if (viewAsAdmin) {
          // Chief doctor viewing as admin: show all appointments
          params.push('view_as_admin=true');
        } else {
          // Chief doctor NOT viewing as admin: API will filter by doctor_id automatically
          // Don't send doctor_id parameter - let API handle it based on view_as_admin absence
        }
      } else if (!viewAsAdmin && currentUser) {
        // Regular doctor or clinic_login: filter by doctor_id
        const roleLower = (userRole || '').toLowerCase();
        if (roleLower === 'doctor' || roleLower === 'clinic_login') {
          params.push(`doctor_id=${currentUser.id}`);
        }
      }
      
      if (params.length > 0) {
        url = `get_appointments_count.php?${params.join('&')}`;
      }
      
      const data = await apiRequest(url);
      if (data.success) {
        setAppointmentsCount(data.total || 0);
        // Also update scheduled count from the response if available
        if (data.scheduled !== undefined) {
          setScheduledAppointmentsCount(data.scheduled || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      setAppointmentsCount(0);
      setScheduledAppointmentsCount(0);
    }
  }, [viewAsAdmin, isChiefDoctor, currentUser, userRole]);

  const fetchAppointments = useCallback(async () => {
    try {
      // If viewAsAdmin is true for chief_doctor, fetch all appointments like admin
      // Otherwise, filter by doctor_id if role is doctor and can't view other patients
      let url = 'get_appointments_list.php';
      const params = [];
      
      const canViewOthers = profile?.can_view_other_patients || hospitalUser?.can_view_other_patients;
      if ((!viewAsAdmin && roleLower === 'doctor' && !canViewOthers) || (!viewAsAdmin && (roleUpper === 'CLINIC_LOGIN' || roleLower === 'clinic_login'))) {
        params.push(`doctor_id=${currentUser.id}`);
      } else if (!viewAsAdmin && isChiefDoctor) {
        // Chief doctor viewing as doctor - only show their appointments
        params.push(`doctor_id=${currentUser.id}`);
      }
      
      // Add view_as_admin parameter for chief_doctor viewing as admin
      if (viewAsAdmin && isChiefDoctor) {
        params.push('view_as_admin=true');
      }
      
      if (params.length > 0) {
        url = `get_appointments_list.php?${params.join('&')}`;
      }
      
      const data = await apiRequest(url);
      if (data.success) {
        const appointmentsList = data.appointments || [];
        setAppointments(appointmentsList);
      } else {
        console.error('Failed to fetch appointments:', data.error);
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  }, [viewAsAdmin, isChiefDoctor, currentUser, userRole, roleLower, roleUpper, profile, hospitalUser]);

  // Recalculate scheduled appointments count whenever appointments change
  useEffect(() => {
    // Count scheduled appointments
    const scheduled = appointments.filter(apt => {
      const status = (apt.status || '').toLowerCase();
      return status === 'scheduled';
    }).length;
    
    // Debug logging
    console.log('DoctorDashboard - viewAsAdmin:', viewAsAdmin, 'isChiefDoctor:', isChiefDoctor);
    console.log('DoctorDashboard - Total appointments:', appointments.length);
    console.log('DoctorDashboard - Scheduled appointments count:', scheduled);
    if (appointments.length > 0) {
      console.log('DoctorDashboard - Sample appointment statuses:', 
        appointments.slice(0, 3).map(apt => ({ id: apt.id, status: apt.status }))
      );
    }
    
    setScheduledAppointmentsCount(scheduled);
  }, [appointments, viewAsAdmin, isChiefDoctor]);

  const fetchSubmissionsCount = useCallback(async () => {
    try {
      // Add view_as_admin parameter for chief_doctor viewing as admin
      let url = 'get_submissions_count.php';
      if (viewAsAdmin && isChiefDoctor) {
        url = 'get_submissions_count.php?view_as_admin=true';
      }
      const data = await apiRequest(url);
      if (data.success) {
        setSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch submissions count:', data.error);
        setSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching submissions count:', error);
      setSubmissionsCount(0);
    }
  }, [viewAsAdmin, isChiefDoctor]);

  const fetchAttendanceCount = useCallback(async (overrideViewAsAdmin = null) => {
    try {
      const shouldViewAsAdmin = overrideViewAsAdmin !== null ? overrideViewAsAdmin : viewAsAdmin;
      const url = shouldViewAsAdmin && isChiefDoctor
        ? 'get_attendance.php?limit=1&offset=0&view_as_admin=true'
        : 'get_attendance.php?limit=1&offset=0';
      const data = await apiRequest(url);
      if (data.success) {
        setAttendanceCount(data.total || 0);
      } else {
        console.error('Failed to fetch attendance count:', data.error);
        setAttendanceCount(0);
      }
    } catch (error) {
      console.error('Error fetching attendance count:', error);
      setAttendanceCount(0);
    }
  }, [viewAsAdmin, isChiefDoctor, userRole]);

  const fetchLeaveRequestsCount = useCallback(async () => {
    try {
      const data = await apiRequest('get_leave_requests.php?limit=1000&offset=0&view_as_admin=true');
      if (data.success) {
        const leaveRequests = data.leave_requests || [];
        setLeaveRequestsCount(leaveRequests.length);
        // Case-insensitive status comparison
        const pending = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'pending').length;
        const approved = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'approved').length;
        setLeaveRequestsPendingCount(pending);
        setLeaveRequestsApprovedCount(approved);
      } else {
        console.error('Failed to fetch leave requests count:', data.error);
        setLeaveRequestsCount(0);
        setLeaveRequestsPendingCount(0);
        setLeaveRequestsApprovedCount(0);
      }
    } catch (error) {
      console.error('Error fetching leave requests count:', error);
      setLeaveRequestsCount(0);
      setLeaveRequestsPendingCount(0);
      setLeaveRequestsApprovedCount(0);
    }
  }, []);

  const fetchCampSubmissionsCount = useCallback(async (overrideViewAsAdmin = null) => {
    try {
      // Use overrideViewAsAdmin if provided, otherwise use current viewAsAdmin state
      const shouldViewAsAdmin = overrideViewAsAdmin !== null ? overrideViewAsAdmin : viewAsAdmin;
      
      // If viewAsAdmin is true for chief_doctor, fetch all submissions like admin
      const url = shouldViewAsAdmin && isChiefDoctor 
        ? 'get_camp_submissions.php?limit=1&offset=0&view_as_admin=true'
        : 'get_camp_submissions.php?limit=1&offset=0';
      
      console.log('Fetching camp submissions count:', {
        url,
        shouldViewAsAdmin,
        overrideViewAsAdmin,
        currentViewAsAdmin: viewAsAdmin,
        role: userRole
      });
      
      const data = await apiRequest(url);
      
      console.log('Camp submissions count response:', data);
      
      if (data && data.success !== false) {
        const count = data.total || 0;
        console.log('Setting camp submissions count to:', count);
        setCampSubmissionsCount(count);
      } else {
        console.error('Failed to fetch camp submissions count:', data?.error || 'Unknown error');
        setCampSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching camp submissions count:', error);
      setCampSubmissionsCount(0);
    }
  }, [viewAsAdmin, isChiefDoctor, userRole]);

  // Refresh submissions after successful submission (defined after fetchSubmissionsCount)
  const refreshSubmissions = useCallback(() => {
    fetchSubmissions();
    fetchSubmissionsCount();
  }, [fetchSubmissions, fetchSubmissionsCount]);

  useEffect(() => {
    console.log('DoctorDashboard useEffect: Current User:', currentUser);
    if (currentUser) {
      console.log('DoctorDashboard: User role:', userRole);
      fetchSubmissions();
      if (roleUpper === 'ADMIN' || roleLower === 'admin' || roleLower === 'doctor' || isChiefDoctor || roleUpper === 'CLINIC_LOGIN' || roleLower === 'clinic_login') {
        console.log('DoctorDashboard: Fetching data for role:', userRole);
        // When viewAsAdmin is true, fetch all patients (no filter) and all doctors
        // When viewAsAdmin is false, fetch only the current user's patients
        if (viewAsAdmin && isChiefDoctor) {
          // Fetch all patients when viewing as admin (pass viewAsAdmin flag)
          // Pass null for addedByFilter to get all patients, and true for viewAsAdmin
          fetchPatients(null, true);
          fetchDoctors();
        } else {
          // Regular doctor view: check if they can view other patients
          const canViewOthers = profile?.can_view_other_patients || hospitalUser?.can_view_other_patients;
          const currentUserId = currentUser?.id;
          
          if (canViewOthers) {
            // If permission granted, fetch all patients (AdminPatientsTab will handle filtering)
            fetchPatients(null, viewAsAdmin);
          } else if (currentUserId) {
            // If no permission, fetch only their own patients
            fetchPatients(currentUserId, viewAsAdmin);
          } else {
            // Fallback: AdminPatientsTab will handle filtering
            fetchPatients(null, viewAsAdmin);
          }
        }
        fetchAppointmentsCount();
        fetchAppointments();
        fetchSubmissionsCount();
        fetchAttendanceCount();
        if (viewAsAdmin && isChiefDoctor) {
          fetchLeaveRequestsCount();
        }
      } else {
        console.warn('DoctorDashboard: Role not allowed:', userRole);
      }
    } else {
      console.log('DoctorDashboard: No user available');
    }
  }, [currentUser, userRole, roleUpper, roleLower, isChiefDoctor, fetchSubmissions, fetchPatients, fetchDoctors, viewAsAdmin, fetchAppointmentsCount, fetchAppointments, fetchSubmissionsCount, fetchAttendanceCount, fetchLeaveRequestsCount]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch camp submissions count when viewAsAdmin or userRole changes
  useEffect(() => {
    if (currentUser && (roleUpper === 'ADMIN' || roleLower === 'admin' || roleLower === 'doctor' || isChiefDoctor || roleUpper === 'CLINIC_LOGIN' || roleLower === 'clinic_login')) {
      console.log('useEffect triggered - fetching camp submissions count, viewAsAdmin:', viewAsAdmin, 'role:', userRole);
      fetchCampSubmissionsCount();
      fetchAttendanceCount();
      if (viewAsAdmin && isChiefDoctor) {
        fetchLeaveRequestsCount();
      }
    }
  }, [currentUser, userRole, viewAsAdmin, fetchCampSubmissionsCount, fetchAttendanceCount]);

  // Filter submissions based on viewAsAdmin mode
  const doctorSubmissions = React.useMemo(() => {
    if (viewAsAdmin || roleUpper === 'ADMIN' || roleLower === 'admin') {
      // Show all submissions when viewing as admin
      return submissions;
    } else if (isChiefDoctor) {
      // Chief doctor viewing as doctor - only show their submissions
      return submissions.filter(sub => sub.doctor_id === currentUser?.id);
    } else {
      // Regular doctor - only their submissions
      return submissions.filter(sub => sub.doctor_id === currentUser?.id);
    }
  }, [submissions, viewAsAdmin, userRole, currentUser]);

  // When viewAsAdmin is true, use exact same tabs as AdminDashboard
  // When viewAsAdmin is false, use doctor tabs (conditionally show camp_submissions and attendance based on permissions)
  const baseTabs = viewAsAdmin && isChiefDoctor 
    ? [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        // Only show Doctors tab for hospital admins (chief_doctor, admin)
        ...(isChiefDoctor || roleUpper === 'ADMIN' || roleLower === 'admin' ? [{ id: 'doctors', label: 'Doctors', icon: Users }] : []),
        { id: 'patients', label: 'Patients', icon: Stethoscope },
        { id: 'appointments', label: 'Appointments', icon: Calendar },
        { id: 'submissions', label: 'Appointment History', icon: FileText },
        { id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList },
        { id: 'attendance', label: 'Attendance', icon: Clock },
        { id: 'settings', label: 'Settings', icon: Settings }
      ]
    : [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'patients', label: 'Patients', icon: Users },
        { id: 'appointments', label: 'Appointments', icon: Calendar },
        { id: 'submissions', label: 'Appointment History', icon: FileText },
        // Conditionally add camp_submissions and attendance based on permissions
        ...(currentUser?.can_access_camp_submissions ? [{ id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList }] : []),
        ...(currentUser?.can_access_attendance ? [{ id: 'attendance', label: 'Attendance', icon: Clock }] : []),
        { id: 'settings', label: 'Settings', icon: Settings }
      ];
  
  // Use baseTabs as tabs (for compatibility with existing code)
  const tabs = baseTabs;

  const handleTabChange = (tabId, subTab = null) => {
    // Handle attendance tab navigation first (before setting activeTab)
    if (tabId === 'attendance') {
      if (subTab) {
        // Set the specific sub-tab (e.g., 'approve_leave')
        setAttendanceInitialTab(subTab);
        // Force remount by updating key if already on attendance tab
        if (activeTab === 'attendance') {
          setAttendanceKey(prev => prev + 1);
        }
      } else if (activeTab === 'overview') {
        // Default to checkin when navigating from overview without subTab
        setAttendanceInitialTab('checkin');
      }
    } else if (tabId !== 'attendance') {
      // Reset to 'checkin' when navigating away from attendance
      setAttendanceInitialTab('checkin');
    }
    
    // If navigating to camp_submissions from overview (via count card), set initial tab to 'list'
    if (tabId === 'camp_submissions' && activeTab === 'overview') {
      setCampSubmissionsInitialTab('list');
    } else if (tabId !== 'camp_submissions') {
      // Reset to 'form' when navigating away
      setCampSubmissionsInitialTab('form');
    }
    setActiveTab(tabId);
  };

  const handleLogout = async () => {
    // Get hospital slug before clearing session
    const hospitalSlug = hospital?.slug || branding?.slug;
    
    if (hospitalUser) {
      await hospitalSignOut();
    } else {
      await signOut();
    }
    
    // Redirect to hospital landing page if slug is available, otherwise to dental hub landing page
    if (hospitalSlug) {
      navigate(`/${hospitalSlug}`);
    } else {
      navigate('/');
    }
    
    toast({
      title: 'Logged out successfully',
      description: 'See you next time!'
    });
  };

  const handleSubmissionClick = (submission) => {
    setSelectedSubmission(submission);
    setFeedback(submission.doctor_feedback || '');
    setRecommendations(submission.recommendations || '');
    setNextAppointment(submission.next_appointment || '');
    setSubmissionStatus(submission.status || 'pending');
  };

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;

    const updates = {
      doctor_feedback: feedback,
      recommendations: recommendations,
      next_appointment: nextAppointment,
      status: submissionStatus,
      reviewed_at: new Date().toISOString(),
    };

    const { error } = await updateSubmission(selectedSubmission.id, updates);
    
    if (!error) {
      toast({
        title: 'Submission updated successfully',
        description: 'Patient has been notified of your feedback'
      });
      setSelectedSubmission(null);
    } else {
      toast({
        title: 'Error updating submission',
        variant: 'destructive'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Doctor Dashboard - Dental Hub</title>
        <meta name="description" content="Manage patient submissions, review dental health forms, and provide medical feedback through your doctor dashboard." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-1 sm:space-x-2">
                {getLogoUrl() ? (
                  <img 
                    src={getLogoUrl()} 
                    alt={`${branding?.name || 'Dental Hub'} Logo`} 
                    className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.style.display = 'flex';
                      }
                    }}
                  />
                ) : (
                  <>
                    <div className="h-8 sm:h-10 md:h-12 w-8 sm:w-10 md:w-12 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                      <Stethoscope className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                    </div>
                    <span className="text-base sm:text-xl font-bold gradient-text">{branding?.name || 'Dental Hub'}</span>
                  </>
                )}
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* View as Admin checkbox - only for chief_doctor */}
                {isChiefDoctor && (
                  <label className="flex items-center space-x-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={viewAsAdmin}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setViewAsAdmin(newValue);
                        // Reset to overview tab when toggling
                        handleTabChange('overview');
                        
                        // Refetch all data when toggling viewAsAdmin
                        if (newValue && isChiefDoctor) {
                          // Viewing as admin: fetch all data
                          fetchDoctors();
                          fetchPatients(null, true); // All patients
                          fetchAppointments(); // Will include view_as_admin=true
                          fetchSubmissions();
                          fetchAppointmentsCount();
                          fetchSubmissionsCount();
                          fetchCampSubmissionsCount(newValue);
                          fetchAttendanceCount(newValue);
                          fetchLeaveRequestsCount();
                        } else {
                          // Viewing as doctor: check if they can view other patients
                          const canViewOthers = profile?.can_view_other_patients || hospitalUser?.can_view_other_patients;
                          const currentUserId = currentUser?.id;
                          
                          if (canViewOthers) {
                            // If permission granted, fetch all patients (AdminPatientsTab will handle filtering)
                            fetchPatients(null, false);
                          } else if (currentUserId) {
                            // If no permission, fetch only their own patients
                            fetchPatients(currentUserId, false);
                          } else {
                            fetchPatients(null, false);
                          }
                          fetchAppointments(); // Will filter by doctor_id
                          fetchSubmissions();
                          fetchAppointmentsCount();
                          fetchSubmissionsCount();
                          fetchCampSubmissionsCount(newValue);
                          fetchAttendanceCount(newValue);
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 cursor-pointer"
                    />
                    <span className="text-white text-xs sm:text-sm group-hover:text-blue-400 transition-colors">
                      View as Admin
                    </span>
                  </label>
                )}
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-xs sm:text-sm">
                    Welcome, {(() => {
                      const userName = currentUser?.full_name || currentUser?.name || 'Doctor';
                      const userRoleLower = (userRole || '').toLowerCase();
                      const isDoctor = userRoleLower === 'doctor' || userRoleLower === 'chief_doctor';
                      return isDoctor ? `Dr. ${userName}` : userName;
                    })()}
                  </span>
                  {userRole && (
                    <span className="text-gray-400 text-xs mt-0.5">
                      {userRole === 'CHIEF_DOCTOR' || userRole === 'chief_doctor' ? 'Chief Doctor' :
                       userRole === 'CLINIC_ADMIN' || userRole === 'clinic_admin' ? 'Clinic Admin' :
                       userRole === 'CLINIC_LOGIN' || userRole === 'clinic_login' ? 'Clinic Login' :
                       userRole === 'admin' ? 'Admin' :
                       userRole === 'doctor' ? 'Doctor' :
                       userRole}
                    </span>
                  )}
                  {currentUser?.doctor_unique_id && (
                    <span className="text-gray-400 text-xs mt-0.5">Doctor Unique ID: {currentUser.doctor_unique_id}</span>
                  )}
                </div>
                <span className="sm:hidden text-white text-xs truncate max-w-[120px]">
                  {(() => {
                    const userName = currentUser?.full_name || currentUser?.name || 'Doctor';
                    const userRoleLower = (userRole || '').toLowerCase();
                    const isDoctor = userRoleLower === 'doctor' || userRoleLower === 'chief_doctor';
                    return isDoctor ? `Dr. ${userName}` : userName;
                  })()}
                </span>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2 sm:px-3 sm:py-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  {viewAsAdmin && isChiefDoctor ? 'Admin Dashboard' : 'Doctor Dashboard'}
                </h1>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                  {viewAsAdmin && isChiefDoctor 
                    ? 'Manage your dental practice and system settings'
                    : 'Manage patients, review submissions, and provide medical feedback'}
                </p>
              </div>
              <div className="text-right">
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {currentDateTime.toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                  <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                    {currentDateTime.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Trial Period Active Section - Only for Chief Doctor with View as Admin checked */}
          {isChiefDoctor && viewAsAdmin && planType === 'TRIAL' && trialInfo && trialInfo.isActive && trialInfo.daysLeft > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <Card className="glass-effect border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="h-6 w-6 text-yellow-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">Trial Period Active</h3>
                      <p className="text-gray-300 mb-3">
                        Your trial period ends on <strong className="text-white">{trialInfo.endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                      </p>
                      <p className="text-yellow-400 font-semibold mb-4">
                        {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'} left
                      </p>
                      <p className="text-gray-300 mb-4">
                        Please enroll before your trial period ends to continue using our services.
                      </p>
                      <Link to={`/pricing?hospital=${hospital?.slug || branding?.slug}`}>
                        <Button size="lg" className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:opacity-90">
                          Enroll Now
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {activeTab === 'overview' && (
              viewAsAdmin && isChiefDoctor ? (
                // Admin Overview Tab when viewing as admin
                  <AdminOverviewTab 
                  stats={{
                    total: submissionsCount || submissions.length,
                    inProgress: submissions.filter(s => s.status === 'in-progress').length,
                    completed: submissions.filter(s => s.status === 'completed').length,
                    activeDoctors: doctors.filter(d => d.status === 'Active').length,
                    activeDoctorsDisplay: `${doctors.filter(d => d.status === 'Active').length}(${doctors.length})`,
                    totalDoctors: doctors.length,
                    totalPatients: patients.length,
                    totalAppointments: appointmentsCount,
                    campSubmissions: campSubmissionsCount
                  }}
                  submissions={submissions}
                  patients={patients}
                  appointments={appointments}
                  doctors={doctors}
                  appointmentsCount={appointmentsCount}
                  scheduledAppointmentsCount={scheduledAppointmentsCount}
                  campSubmissionsCount={campSubmissionsCount}
                  attendanceCount={attendanceCount}
                  leaveRequestsCount={leaveRequestsCount}
                  leaveRequestsPendingCount={leaveRequestsPendingCount}
                  leaveRequestsApprovedCount={leaveRequestsApprovedCount}
                  onNavigate={handleTabChange}
                />
              ) : (
                // Doctor Overview Tab when viewing as doctor
                <>
                  {/* Stats Cards */}
                  <DoctorStatsCards 
                    doctorSubmissions={doctorSubmissions} 
                    patients={patients}
                    appointments={appointments}
                    appointmentsCount={appointmentsCount}
                    scheduledAppointmentsCount={scheduledAppointmentsCount}
                    submissionsCount={submissionsCount}
                    campSubmissionsCount={campSubmissionsCount}
                    attendanceCount={attendanceCount}
                    onNavigate={handleTabChange}
                    canAccessCampSubmissions={currentUser?.can_access_camp_submissions || false}
                    canAccessAttendance={currentUser?.can_access_attendance || false}
                  />

                  <div className="flex flex-col lg:flex-row gap-8 mt-8">
                    {/* Patient Submissions - 65% width */}
                    <div className="w-full lg:w-[65%]">
                      <PatientSubmissionsList 
                        doctorSubmissions={doctorSubmissions} 
                        handleSubmissionClick={handleSubmissionClick} 
                        selectedSubmission={selectedSubmission}
                        onRefresh={refreshSubmissions}
                      />
                    </div>

                    {/* Submission Details & Feedback / Doctor Profile - 35% width */}
                    <div className="w-full lg:w-[35%]">
                      <SubmissionDetailsAndFeedback
                        selectedSubmission={selectedSubmission}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        recommendations={recommendations}
                        setRecommendations={setRecommendations}
                        nextAppointment={nextAppointment}
                        setNextAppointment={setNextAppointment}
                        submissionStatus={submissionStatus}
                        setSubmissionStatus={setSubmissionStatus}
                        handleUpdateSubmission={handleUpdateSubmission}
                        setSelectedSubmission={setSelectedSubmission}
                        profile={currentUser}
                      />
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <DoctorRecentActivity 
                    submissions={doctorSubmissions}
                    appointments={appointments}
                    profile={currentUser}
                  />

                  {/* Quick Actions - Hidden */}
                  {/* <DoctorQuickActions /> */}
                </>
              )
            )}
            {activeTab === 'submissions' && (
              viewAsAdmin && isChiefDoctor ? (
                <AdminSubmissionsTab submissions={submissions} />
              ) : (
                <PatientSubmissionsList 
                  doctorSubmissions={doctorSubmissions} 
                  handleSubmissionClick={handleSubmissionClick} 
                  selectedSubmission={selectedSubmission}
                  onRefresh={refreshSubmissions}
                />
              )
            )}
            {/* Show Doctors tab when viewing as admin or for admins */}
            {activeTab === 'doctors' && (viewAsAdmin && isChiefDoctor || roleUpper === 'ADMIN' || roleLower === 'admin') && (
              <AdminDoctorsTab doctors={doctors} addDoctor={addDoctor} updateDoctor={updateDoctor} viewAsAdmin={viewAsAdmin && isChiefDoctor} />
            )}
            {activeTab === 'patients' && <AdminPatientsTab onSubmissionComplete={refreshSubmissions} viewAsAdmin={viewAsAdmin} />}
            {activeTab === 'appointments' && <AppointmentsTab viewAsAdmin={viewAsAdmin} />}
            {activeTab === 'camp_submissions' && <CampSubmissions initialTab={campSubmissionsInitialTab} viewAsAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'attendance' && <Attendance key={`attendance-${attendanceInitialTab}-${attendanceKey}`} initialTab={attendanceInitialTab} viewAsAdmin={viewAsAdmin && isChiefDoctor} isAdmin={viewAsAdmin && isChiefDoctor} />}
            {activeTab === 'settings' && (
              viewAsAdmin && isChiefDoctor ? (
                <AdminSettingsTab />
              ) : (
                <DoctorSettingsTab />
              )
            )}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              {getLogoUrl() ? (
                <img 
                  src={getLogoUrl()} 
                  alt={`${branding?.name || 'Dental Hub'} Logo`} 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : (
                <>
                  <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                    <Stethoscope className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-semibold gradient-text">{branding?.name || 'Dental Hub'}</span>
                </>
              )}
            </div>
            <p className="text-gray-400 text-sm">
              ©2026 Dental Hub, Product of  <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>. All rights reserved.
              
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DoctorDashboard;